import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui';
import { CopyBlock, dracula } from '../../components/CodeBlock';

const Authentication = () => {
  const curlExample = `curl -X GET https://api.walytic.com/api/sessions \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

  return (
    <div className="p-2 md:p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-2">Authentication</h1>
      <p className="text-muted-foreground mb-6">
        Every request to the Walytic API must include an API key and the
        workspace/client ID it should operate inside.
      </p>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">1. Get an API key</h2>
        <ol className="list-decimal list-inside text-sm space-y-1 text-gray-700">
          <li>
            Open{' '}
            <Link to="/settings/api-keys" className="text-sky-600 hover:underline">
              Settings → API Keys
            </Link>
            .
          </li>
          <li>Click <strong>Create new key</strong> and give it a descriptive name.</li>
          <li>
            Copy the key <strong>once</strong> — we hash it server-side, so
            you won&apos;t be able to view it again.
          </li>
          <li>Store it in your server&apos;s secret manager (never commit to git).</li>
        </ol>
      </Card>

      <Card className="p-4 my-4 bg-muted">
        <h2 className="font-semibold mb-2">2. Send the API key and workspace headers</h2>
        <p className="text-sm text-gray-700 mb-2">
          Attach both headers on every request. <code className="font-mono">X-Client-Id</code> is accepted as an alias for <code className="font-mono">X-Workspace-Id</code>.
        </p>
        <CopyBlock
          language="bash"
          text={`x-api-key: YOUR_API_KEY\nX-Workspace-Id: YOUR_WORKSPACE_ID`}
          theme={dracula}
          wrapLines
        />
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">3. Try a ping call</h2>
        <p className="text-sm text-gray-700 mb-3">
          The canonical health check is <code className="font-mono">GET /api/sessions</code>{' '}
          — it lists the WhatsApp numbers connected to your workspace:
        </p>
        <CopyBlock language="bash" text={curlExample} theme={dracula} wrapLines />
        <p className="text-sm text-gray-700 mt-3">
          For copy-pasteable examples in Node, Python, PHP, Go and more,
          open the{' '}
          <Link
            to="/integration-and-api/swagger"
            className="text-sky-600 hover:underline"
          >
            interactive API Reference
          </Link>{' '}
          — every endpoint has a built-in code-sample switcher and a Try It
          panel that fires real requests against your account.
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">Common errors</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Meaning</th>
                <th className="px-3 py-2 font-medium">How to fix</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">401</td>
                <td className="px-3 py-2">API key missing</td>
                <td className="px-3 py-2">
                  Add both <code className="font-mono">x-api-key</code> and <code className="font-mono">X-Workspace-Id</code>. Check for typos
                  in the header names.
                </td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">403</td>
                <td className="px-3 py-2">Invalid API key or workspace</td>
                <td className="px-3 py-2">
                  Key was revoked, doesn&apos;t exist, or belongs to a different workspace. Confirm the active workspace ID in{' '}
                  <Link to="/settings/api-keys" className="text-sky-600 hover:underline">
                    Settings → API Keys
                  </Link>
                  .
                </td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">429</td>
                <td className="px-3 py-2">Rate limit exceeded</td>
                <td className="px-3 py-2">
                  Back off and retry. Check your plan limits in{' '}
                  <Link to="/settings/billing" className="text-sky-600 hover:underline">
                    Settings → Billing
                  </Link>
                  .
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4 my-4">
        <h2 className="font-semibold mb-2">JWT cookies vs. API keys</h2>
        <p className="text-sm text-gray-700">
          The Walytic web app authenticates users with a JWT cookie. That cookie
          is <strong>only for the browser</strong> — server-to-server
          integrations should always use <code className="font-mono">x-api-key</code>{' '}
          plus <code className="font-mono">X-Workspace-Id</code> instead. Don&apos;t share or proxy the cookie.
        </p>
      </Card>

      <Card className="p-4 my-4 border-l-4 border-emerald-400 bg-emerald-50">
        <h2 className="font-semibold mb-2 text-emerald-900">
          Webhook authenticity
        </h2>
        <p className="text-sm text-emerald-900">
          Every webhook delivery is signed with HMAC-SHA256. Walytic includes
          header <code className="font-mono">X-Webhook-Signature</code> on each
          POST — its value is the hex-encoded digest of the raw request body,
          computed with the <code>secret</code> returned when you registered
          the webhook. Verify it on your side and reject mismatches.
        </p>
        <p className="text-sm text-emerald-900 mt-2">
          See the{' '}
          <Link to="/integration-and-api/swagger" className="text-emerald-900 underline">
            Webhooks section in the API Reference
          </Link>{' '}
          for the full request/response shape and event-name catalogue.
        </p>
      </Card>
    </div>
  );
};

export default Authentication;
